import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { ProTablePlusForm } from '@mpc/common/src/components';
import { useControllableValue } from 'ahooks';
import useDesignatedExpertColumns from '../cells/useDesignatedExpertColumns';
import { FullScreenButton } from '@mpc/components';
import { ProCard, useScreenAdapterContext } from '@bedrock/eed-react';
import SplitDesignatedExpert from './SplitDesignatedExpert';

type DesignatedExpertType =
  'split' // 拆单
  | 'detail' // 详情页
  | 'pendingSourceAssess' // 商务一询Bottom
  | 'supplierDetail' // 供应商一询详情
  | 'addPlanData' // 手动添加弹框
interface IProps {
  value?: any[];
  onChange?: (value: any[]) => void;
  view?: boolean;
  style?: React.CSSProperties;
  className?: string;
  showFullScreen?: boolean;
  handleFullChange?: (isFullscreen: boolean) => void;
  type?: DesignatedExpertType;
  l3_category_id?: any;
  proCardProps?: any;
}

function getDuplicateIndexes(arr, key) {
  if (!arr?.length) return [];
  const valueIndexMap = new Map();
  arr.forEach((item, idx) => {
    let v = item[key];
    if (v === null || v === undefined || v.trim() === '') return;
    v = v.trim();
    if (!valueIndexMap.has(v)) {
      valueIndexMap.set(v, []);
    }
    valueIndexMap.get(v).push(idx);
  });
  const result = [];
  valueIndexMap.forEach((indexes, val) => {
    if (indexes.length > 1) {
      result.push({
        sameValue: val,
        sameIndex: indexes,
      });
    }
  });
  return result;
}

const ProCardOrDiv = ({ isProCard, ...props }) => {
  return isProCard ? <ProCard {...props} /> : <div {...props} />
}

const DesignatedExpert: React.FC<IProps> = forwardRef((props, ref) => {
  const { view, style, className, showFullScreen, type, l3_category_id } = props;
  const tableRef = React.useRef<any>();
  const [state, setState] = useControllableValue<any[]>(props, {
    defaultValue: [],
  });
  const { viewBase } = useScreenAdapterContext();

  const { columnsConfig, loading } = useDesignatedExpertColumns({
    view,
    changeData: setState,
    l3_category_id,
  });
  const fullRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useImperativeHandle(ref, () => ({
    validateFields: async () => {
      try {
        if (tableRef.current?.validateFieldsAndScroll) {
          const values = await tableRef.current?.validateFieldsAndScroll?.();
          const duplicates = getDuplicateIndexes(state, 'main_platform_link');
          if (duplicates.length > 0) {
            const duplicateMessages = duplicates.map(dup => {
              const rowNumbers = dup.sameIndex.map(idx => idx + 1).join('、');
              return `第${rowNumbers}行相同`;
            });
            return Promise.reject(`达人链接存在重复：${duplicateMessages.join('，')}`)
          }
          if (values?.[0]?.message) {
            return Promise.reject(values?.[0]?.message?.replace('【[object Object]】', ''))
          }
        }
        return Promise.resolve()
      } catch (error) {
        console.log(error, 'error');
        return Promise.reject()
      }
    }
  }))

  const handleFullChange = (isFullscreen: boolean) => {
    setIsFullscreen(isFullscreen);
    props.handleFullChange?.(isFullscreen);
  }

  const columns = useMemo(() => {
    // 手动添加弹框
    if (type === 'addPlanData') {
      return [
        columnsConfig.index,
        {
          ...columnsConfig.main_platform_link,
          fixed: 'left' as const
        },
        {
          ...columnsConfig.main_platform_account,
          fixed: 'left' as const
        },
        columnsConfig.main_platform,
        columnsConfig.public_order,
        columnsConfig.main_platform_quote_item,
        {
          title: '操作',
          dataIndex: 'actions',
          width: 60 * viewBase,
          fixed: 'right',
          render: (v: any, record: any, index: number) => {
            return <Button type="link" danger onClick={() => setState(state => state.filter((s, i) => i !== index))}>删除</Button>
          }
        },
      ]
    }

    // 详情
    if (type === 'detail') {
      return [
        columnsConfig.index,
        {
          ...columnsConfig.profile_of_the_expert,
          fixed: 'left' as const
        },
        columnsConfig.cooperation_project,
        columnsConfig.number_of_fans,
        columnsConfig.historical_cooperation,
        columnsConfig.platform_price,
        columnsConfig.CPM,
      ]
    }

    // 商务一询bottom
    if (type === 'pendingSourceAssess') {
      return [
        columnsConfig.index,
        {
          ...columnsConfig.profile_of_the_expert,
          fixed: 'left' as const
        },
        columnsConfig.is_exclusive_supplier,
        columnsConfig.supplier_list,
        columnsConfig.cooperation_project,
        columnsConfig.number_of_fans,
        columnsConfig.historical_cooperation,
        columnsConfig.platform_price,
        columnsConfig.CPM,
      ]
    }

    // 供应商一询详情
    if (type === 'supplierDetail') {
      return [
        columnsConfig.index,
        {
          ...columnsConfig.profile_of_the_expert,
          fixed: 'left' as const
        },
        columnsConfig.main_platform_link,
        columnsConfig.main_platform,
        columnsConfig.public_order,
        columnsConfig.main_platform_quote_item,
      ]
    }

    return []
  }, [columnsConfig, state]);

  if (type === 'split') {
    return <SplitDesignatedExpert
      {...props}
      state={state}
      setState={setState}
      handleFullChange={handleFullChange}
      isFullscreen={isFullscreen}
      tableRef={tableRef}
    />
  }

  return (
    <div style={{ width: '100%', ...style }} className={className}>
      <div ref={fullRef}>
        <ProCardOrDiv
          title={
           `指定达人${view ? `(${state?.length || 0})` : ''}`
          }
          rightSlot={showFullScreen && <FullScreenButton fullRef={fullRef} handleFullChange={handleFullChange} />}
          level={2}
          noPadding
          {...props?.proCardProps}
          isProCard={type !== 'addPlanData'}
        >
          <ProTablePlusForm
            ref={tableRef}
            style={{ width: '100%' }}
            scroll={{ 'x': 'max-content', y: isFullscreen ? window.innerHeight - 140 * viewBase : 560 }}
            value={state}
            onChange={setState}
            columns={columns}
            isShowLoading={loading}
            virtual={type !== 'addPlanData' ? { maxRows: 0, useAntd: false, itemHeight: 123 } : undefined }
            verticalBorder
          />
          {
            type === 'addPlanData' && <Button
              className='mt-[8px]'
              size='medium'
              type='checked'
              icon={<Plus />}
              onClick={() => {
                setState([...(state || []), {
                  import_type: 'FROM_PLAN',
                  add_form_not_included: true,
                }])
              }}
            >
              新增一行
            </Button>
          }
        </ProCardOrDiv>
      </div>
    </div>
  )
})

export default DesignatedExpert;
